import React from 'react'
import styled from '@emotion/styled'

import {Divider} from '@vio/atlas/Divider'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {Brand} from '@vio/core/src/brand/types'

import {BrandLogo} from '../BrandLogo'
import {FooterLink, useFooterLegalLinks} from './FooterLegalLinks'
import {FooterSocialButton} from './FooterSocialButton'

interface Props {
  brand: Brand
  showHowToCancel?: boolean
}

export const HotelixFooter = ({brand, showHowToCancel}: Props) => {
  const legalLinks = useFooterLegalLinks(brand, showHowToCancel)
  const currentYear = new Date().getFullYear()

  return (
    <Wrapper>
      <Layout>
        <Socials>
          <FooterSocialButton
            url={brand.socials?.instagramUrl}
            iconName="Instagram"
          />
          <FooterSocialButton
            url={brand.socials?.facebookUrl}
            iconName="Facebook"
          />
          <FooterSocialButton
            url={brand.socials?.linkedinUrl}
            iconName="Linkedin"
          />
        </Socials>
        <Links>
          {legalLinks.map(link => (
            <li key={link.label}>
              <FooterLink {...link} />
            </li>
          ))}
        </Links>
        <DividerCell>
          <Divider />
        </DividerCell>
        <Logo>
          <BrandLogo brand={brand} isInverse />
        </Logo>
        <Copyright className="chromatic-ignore">
          © {currentYear} {brand.name}
        </Copyright>
      </Layout>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  color: ${cssTheme.colors.content.neutral.inverse};
  background-color: ${cssTheme.colors.background.brand.inverse};
  padding: ${cssTheme.layout.spacing.s950} ${cssTheme.layout.spacing.s600}
    ${cssTheme.layout.spacing.s900};

  ${linariaMq.desktopSm} {
    padding-bottom: 96px;
  }
`

const Layout = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  grid-template-areas:
    'socials socials'
    'links links'
    'divider divider'
    'logo copyright';
  align-items: center;
  row-gap: ${cssTheme.layout.spacing.s600};
  max-width: 1120px;
  margin: auto;

  ${linariaMq.desktopSm} {
    grid-template-areas:
      'logo socials'
      'divider divider'
      'links copyright';
    row-gap: ${cssTheme.layout.spacing.s800};
  }
`

const Socials = styled.div`
  grid-area: socials;
  justify-self: center;
  display: flex;
  gap: ${cssTheme.layout.spacing.s500};
  margin-bottom: ${cssTheme.layout.spacing.s600};

  ${linariaMq.desktopSm} {
    justify-self: end;
    margin-bottom: 0;
  }
`

const Links = styled.ul`
  grid-area: links;
  justify-self: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s600};
  list-style-type: none;
  margin: 0 0 ${cssTheme.layout.spacing.s600};
  padding: 0;

  ${linariaMq.desktopSm} {
    justify-self: start;
    flex-direction: row;
    margin: 0;
  }
`

const DividerCell = styled.div`
  grid-area: divider;
`

const Logo = styled.div`
  grid-area: logo;
  justify-self: start;
  height: 20px;

  img {
    height: 100%;
    width: auto;
  }

  ${linariaMq.desktopSm} {
    height: 32px;
  }
`

const Copyright = styled.div`
  grid-area: copyright;
  justify-self: end;
  ${cssTheme.typography.labelXS};

  ${linariaMq.desktopSm} {
    ${cssTheme.typography.bodyS};
  }
`
